/**
 * ===== SERVICE D'OPTIMISATION MOBILE =====
 * Améliore les performances et l'UX sur mobile;
 */
export class MobileOptimizationService {
  private static instance: MobileOptimizationService;
  private isMobile: boolean = false;
  private isTouchDevice: boolean = false;
  private viewportWidth: number = 0;
  private viewportHeight: number = 0;
  private constructor() {
    this.detectDevice();
    this.setupEventListeners();
    this.optimizeForMobile();
  }
  public static getInstance(): MobileOptimizationService {
    if (!MobileOptimizationService.instance) {
      MobileOptimizationService.instance = new MobileOptimizationService();
    }
    return MobileOptimizationService.instance;
  }
  private detectDevice(): void {
    this.isMobile = window.innerWidth <= 768;
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
  }
  private setupEventListeners(): void {
    window.addEventListener('resize', this.handleResize.bind(this));
    window.addEventListener('orientationchange', this.handleOrientationChange.bind(this));
  }
  private handleResize(): void {
    this.detectDevice();
    this.optimizeForMobile();
  }
  private handleOrientationChange(): void {
    setTimeout(() => {
      this.detectDevice();
      this.optimizeForMobile();
    }, 100);
  }
  private optimizeForMobile(): void {
    if (this.isMobile) {
      this.enableMobileOptimizations();
    } else {
      this.disableMobileOptimizations();
    }
  }
  private enableMobileOptimizations(): void {
    // Optimisation du grid layout pour mobile;
    this.optimizeGridLayout();
    // Lazy loading des fonts;
    this.enableFontLazyLoading();
    // Optimisation des animations;
    this.optimizeAnimations();
    // Touch gestures;
    this.enableTouchGestures();
    // Bottom sheet pour panels;
    this.enableBottomSheet();
    // Haptic feedback;
    this.enableHapticFeedback();
  }
  private disableMobileOptimizations(): void {
    // Désactiver les optimisations mobile;
    document.body.classList.remove('mobile-optimized');
  }
  private optimizeGridLayout(): void {
    const toolGrids = document.querySelectorAll('.tool-grid');
    toolGrids.forEach(grid => {
      if (this.viewportWidth <= 480) {
        grid.classList.add('grid-cols-1');
        grid.classList.remove('grid-cols-2', 'grid-cols-3');
      } else if (this.viewportWidth <= 768) {
        grid.classList.add('grid-cols-2');
        grid.classList.remove('grid-cols-1', 'grid-cols-3');
      }
    });
  }
  private enableFontLazyLoading(): void {
    // Lazy loading des fonts pour améliorer les performances;
    const fontObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const fontFamily = element.dataset.fontFamily;
          if (fontFamily) {
            this.loadFont(fontFamily);
          }
        }
      });
    });
    document.querySelectorAll('[data-font-family]').forEach(el => {
      fontObserver.observe(el);
    });
  }
  private loadFont(fontFamily: string): void {
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily}:wght@400;500;600;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  private optimizeAnimations(): void {
    // Réduire les animations sur mobile pour de meilleures performances;
    if (this.isMobile) {
      document.documentElement.style.setProperty('--transition-fast', '100ms');
      document.documentElement.style.setProperty('--transition-normal', '150ms');
      document.documentElement.style.setProperty('--transition-slow', '200ms');
    }
  }
  private enableTouchGestures(): void {
    if (!this.isTouchDevice) return;
    // Pinch to zoom sur le canvas;
    this.setupPinchToZoom();
    // Swipe gestures pour navigation;
    this.setupSwipeGestures();
  }
  private setupPinchToZoom(): void {
    let initialDistance = 0;
    let initialScale = 1;
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );
        initialScale = parseFloat(
          getComputedStyle(document.body).getPropertyValue('--canvas-scale') || '1'
        );
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );
        const scale = initialScale * (currentDistance / initialDistance);
        const clampedScale = Math.max(0.5, Math.min(3, scale));
        document.documentElement.style.setProperty('--canvas-scale', clampedScale.toString());
      }
    };
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
  }
  private setupSwipeGestures(): void {
    let startX = 0;
    let startY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const minSwipeDistance = 50;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          this.handleSwipeRight();
        } else {
          this.handleSwipeLeft();
        }
      }
    };
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
  }
  private handleSwipeRight(): void {
    // Ouvrir sidebar ou navigation précédente;
    const sidebar = document.querySelector('.sidebar');
    if (sidebar && sidebar.classList.contains('collapsed')) {
      sidebar.classList.remove('collapsed');
    }
  }
  private handleSwipeLeft(): void {
    // Fermer sidebar ou navigation suivante;
    const sidebar = document.querySelector('.sidebar');
    if (sidebar && !sidebar.classList.contains('collapsed')) {
      sidebar.classList.add('collapsed');
    }
  }
  private enableBottomSheet(): void {
    // Implémentation d'un bottom sheet pour les panels sur mobile;
    const panels = document.querySelectorAll('.properties-panel, .layers-panel');
    panels.forEach(panel => {
      if (this.isMobile) {
        panel.classList.add('bottom-sheet');
      } else {
        panel.classList.remove('bottom-sheet');
      }
    });
  }
  private enableHapticFeedback(): void {
    if ('vibrate' in navigator) {
      // Haptic feedback pour les actions importantes;
      document.addEventListener('click', e => {
        const target = e.target as HTMLElement;
        if (
          target.classList.contains('btn-primary') ||
          target.classList.contains('tool-btn') ||
          target.classList.contains('action-btn')
        ) {
          navigator.vibrate(10); // Vibration légère;
        }
      });
    }
  }
  // Méthodes publiques;
  public isMobileDevice(): boolean {
    return this.isMobile;
  }
  public getIsTouchDevice(): boolean {
    return this.isTouchDevice;
  }
  public getViewportSize(): { width: number; height: number } {
    return { width: this.viewportWidth, height: this.viewportHeight };
  }
  public optimizeCanvasForMobile(): void {
    if (this.isMobile) {
      const canvas = document.querySelector('.canvas-container');
      if (canvas) {
        canvas.classList.add('mobile-canvas');
        // Ajuster la taille du canvas pour mobile;
        const maxWidth = this.viewportWidth - 32; // 16px de marge de chaque côté
        const maxHeight = this.viewportHeight - 200; // Réserver de l'espace pour les contrôles;
        (canvas as HTMLElement).style.maxWidth = `${maxWidth}px`;
        (canvas as HTMLElement).style.maxHeight = `${maxHeight}px`;
      }
    }
  }
  public enableOfflineMode(): void {
    // Indicateur de mode hors ligne;
    const offlineIndicator = document.createElement('div');
    offlineIndicator.className =
      'offline-indicator fixed top-4 right-4 bg-warning text-white px-3 py-2 rounded-md text-sm z-toast hidden';
    offlineIndicator.textContent = 'Mode hors ligne';
    document.body.appendChild(offlineIndicator);
    window.addEventListener('online', () => {
      offlineIndicator.classList.add('hidden');
    });
    window.addEventListener('offline', () => {
      offlineIndicator.classList.remove('hidden');
    });
  }
}
// CSS pour les optimisations mobile;
export const mobileOptimizationsCSS = `
/* === OPTIMISATIONS MOBILE === */
@media (max-width: 768px) {
  .mobile-optimized .tool-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .mobile-optimized .tool-btn {
    min-height: 60px;
    padding: 12px;
  }
  .mobile-optimized .sidebar.collapsed .tool-grid {
    grid-template-columns: 1fr;
  }
  .mobile-optimized .canvas-controls {
    position: fixed;
    bottom: 16px;
    right: 16px;
    top: auto;
    flex-direction: column;
    gap: 8px;
  }
  .mobile-optimized .zoom-control {
    flex-direction: row;
  }
  .mobile-optimized .mobile-canvas {
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 200px,);
  }
}
@media (max-width: 480px) {
  .mobile-optimized .tool-grid {
    grid-template-columns: 1fr;
  }
  .mobile-optimized .tool-btn {
    min-height: 48px;
    flex-direction: row;
    justify-content: flex-start;
    padding: 8px 12px;
  }
  .mobile-optimized .tool-btn i {
    width: 20px;
    height: 20px;
  }
  .mobile-optimized .tool-btn span {
    margin-left: 8px;
  }
}
/* === BOTTOM SHEET === */
.bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 50vh;
  transform: translateY(100%,);
  transition: transform 0.3s ease-out;
  z-index: 1000;
}
.bottom-sheet.open {
  transform: translateY(0,);
}
/* === TOUCH GESTURES === */
.touch-gestures {
  touch-action: pan-x pan-y;
}
.pinch-zoom {
  touch-action: none;
}
/* === HAPTIC FEEDBACK === */
.haptic-feedback: active {
  transform: scale(0.95,);
}
/* === OFFLINE INDICATOR === */
.offline-indicator {
  animation: slideInRight 0.3s ease-out;
}
@keyframes slideInRight {
  from {
    transform: translateX(100%,);
  }
  to {
    transform: translateX(0,);
  }
}
`;
